import { z } from "zod";

/**
 * A goal's whole setup, as one versioned document (goal_versions.config_json). Every setting the
 * owner can change lives here, so a new setting never needs a migration and every change is a new
 * version. Defaults live in shared/goals/defaults.ts, never inline in code that reads the config.
 */

/** When something recurs, in the goal's own timezone. */
export const ScheduleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("daily"), at: z.string().regex(/^\d{2}:\d{2}$/) }),
  z.object({ kind: z.literal("weekly"), day: z.number().int().min(0).max(6), at: z.string().regex(/^\d{2}:\d{2}$/) }),
  z.object({ kind: z.literal("every"), minutes: z.number().int().positive() }),
]);
export type Schedule = z.infer<typeof ScheduleSchema>;

/** One channel the goal uses. The card text is the owner's words; null means the skill's own default. */
export const ChannelSchema = z.object({
  on: z.boolean(),
  card: z
    .object({ for: z.array(z.string()), own: z.string(), ask: z.string() })
    .nullable()
    .default(null),
  rules: z.array(z.string()).default([]),
  markets: z.array(z.string()).default([]),
  settings: z.record(z.string(), z.unknown()).default({}),
});
export type ChannelConfig = z.infer<typeof ChannelSchema>;

/** A library version the goal uses, with the goal's own edits per section (section id to text). */
export const PinSchema = z.object({
  kind: z.enum(["playbook", "skill", "method"]),
  name: z.string(),
  version: z.number().int().positive(),
  itemId: z.string(),
  edits: z.record(z.string(), z.string()).default({}),
});
export type Pin = z.infer<typeof PinSchema>;

export const AutonomySchema = z.enum(["ask", "within_limits", "on_its_own"]);
export type Autonomy = z.infer<typeof AutonomySchema>;

export const GoalConfigSchema = z.object({
  v: z.literal(1).default(1),
  sentence: z.string().min(1),
  result: z.object({
    /** What one result is called, e.g. "registrations". */
    label: z.string().min(1),
    target: z.number().int().positive(),
    /** The deadline as a local date, YYYY-MM-DD. */
    by: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  }),
  money: z.object({
    budgetCents: z.number().int().nonnegative(),
    perResultMaxCents: z.number().int().positive(),
    /** The most a paid channel may spend in a day; null when the goal sets only a total. */
    dailyMaxCents: z.number().int().positive().nullable(),
    toolsCents: z.number().int().nonnegative().default(0),
  }),
  timezone: z.string().min(1),
  rules: z.object({
    /** Stop an ad once it spends this much with no result. */
    stopNoResultCents: z.number().int().positive(),
    /** Call a test only after this many days and clicks. */
    patience: z.object({ days: z.number().int().positive(), clicks: z.number().int().nonnegative() }),
    /** The biggest single raise of a daily amount, in percent. */
    stepUpPct: z.number().positive(),
    callingAndTexting: z.boolean(),
    /** Things the agent never does, in the owner's words. */
    never: z.array(z.string()),
    /** The owner's own extra rules, in their words. */
    own: z.array(z.string()).default([]),
  }),
  rhythm: z.object({
    review: ScheduleSchema,
    replies: ScheduleSchema,
    /** The fewest days between changes to a live ad. */
    liveAdChangeDays: z.number().int().nonnegative(),
    /** When the agent looks for new people. */
    sweep: ScheduleSchema,
  }),
  channels: z.record(z.string(), ChannelSchema),
  library: z.array(PinSchema),
  /** Tools the owner switched on or off for this goal (tool id to on). Missing = the skill's default. */
  tools: z.record(z.string(), z.boolean()).default({}),
  caps: z.object({
    /** The most a single run may spend on data and AI, in micro-dollars. */
    perRunMicros: z.number().int().positive(),
  }),
  /** Per action kind: ask first, act within limits, or act on its own. Missing = the action's default. */
  autonomy: z.record(z.string(), AutonomySchema).default({}),
  /** Per run kind: the model to use. Missing = the account's or Flashadz's default. */
  models: z.record(z.string(), z.string()).default({}),
});
export type GoalConfig = z.infer<typeof GoalConfigSchema>;

/** Parse a stored config; throws with the path of the first problem. */
export function parseGoalConfig(json: string): GoalConfig {
  return GoalConfigSchema.parse(JSON.parse(json));
}
